import type {ThemePresetsKeys} from '@stripe-internal/embedded-theming';
import type OfferFinancingType from '@stripe-internal/capital-offers-sdk/dist/shared/types/OfferFinancingType';
import type {UpdateOptions} from './InitAndUpdateOptionsTypes';
import type {INSTALL_STATES} from '../../components/Apps/constants';
import type {
  EmbeddedError,
  EmbeddedErrorType,
} from '../utils/embeddedLoadErrors';
import type {CustomHtmlElementMethod} from './ConnectElementList';
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface IConnectElementProperties {}

export type ConnectElementEventTypes = {
  // This event is deprecated in favor of 'exit', but is still being emitted for backwards compatibility
  onboardingexited: Record<string, unknown>;
  // Emitted when onboarding is exited
  exit: Record<string, unknown>;
  // Emitted when an account navigates to a different step or section within onboarding
  stepchange: {
    step: string;
  };

  onnotificationschange: {
    total: number;
    actionRequired: number;
  };

  // Emitted when an instant payout is created
  instantpayoutcreated: {
    payoutId: string;
  };

  accountsessionclaimed: {
    merchantId: string;
  };

  appinstallfetched: {
    appId: string;
    state: INSTALL_STATES | null;
  };

  appinstallcompleted: {
    appId: string;
    state: INSTALL_STATES.INSTALLED | INSTALL_STATES.UNINSTALLED;
  };

  // Emitted when the payment details overlay is closed
  close: Record<string, unknown>;

  // Emitted when issuing card is loaded
  issuingcardload: Record<string, unknown>;

  // Emitted when issuing card row is loaded
  issuingcardrowload: Record<string, unknown>;

  // Emitted when a user successfully submits their financing application
  onapplicationsubmitted: Record<string, unknown>;

  // Emitted when the capital financing component loads all the merchant's financings
  onfinancingsloaded: {
    total: number;
  };

  // Emitted when the financing changes, either through default behavior or when user selects a financing from the dropdown
  onchangefinancing: {
    id: string;
    type: OfferFinancingType;
    isDualMerchantFinancing: boolean;
  };

  onapplicationstepchange: {
    step: string;
  };

  // Emitted when payment disputes loads the disputes for a payment
  ondisputesloaded: {
    total: number;
  };

  // Emitted when the capital financing promotion component loads a merchant's offer
  oneligiblefinancingofferloaded: {
    productType: string;
    activeFinancingCount: number;
  };

  // Emitted when the instant payouts promotion component is shown
  oninstantpayoutspromotionloaded: {
    promotionShown: boolean;
  };

  // Emitted when a component fails to load
  loaderror: {
    error: EmbeddedError;
  };

  // For internal use: Emmitted when a component fails to load, with the raw error object
  _internal_loaderror: {
    error: Error;
    type: EmbeddedErrorType;
  };

  // Emitted when a component loads successfully
  loaderstart: Record<string, unknown>;

  // Internal events
  connectjsoptionsupdated: {
    values: UpdateOptions;
    appearanceOptionsOrder: string[];
    invalidAppearanceTokens: string[];
    themePreset: ThemePresetsKeys;
  };
  debugUtilsTestEvent: {
    debugUtilsTestValue: string;
  };

  // Emitted when the account management form section is closed
  accountmanagementformclose: Record<string, unknown>;

  // Emitted when a new tax registration is added
  onaftertaxregistrationadded: {
    id: string;
  };

  // Emitted when a tax registration is expired
  onaftertaxregistrationexpired: {
    id: string;
  };

  // Emitted when tax settings are updated
  ontaxsettingsupdated: {
    id: string;
  };

  // Emitted when an agreement between seller and agent changes state
  onagreementupdated: {
    agreementId: string;
  };

  // Emitted when the Mobile SDK needs to use a Financial Connections flow
  mobilefinancialconnectionsrequested: {
    clientSecret: string;
    id: string;
    connectedAccountId: string;
  };

  // Emitted when the Mobile SDK needs to open a secure webview
  openauthenticatedwebview: {
    id: string;
    url: string;
  };

  onreportavailabilityloaded: {
    isReportAvailable: boolean;
  };

  // Emitted when the user has finished checking out terminal hardware order from shop
  oncheckoutfinished: Record<string, unknown>;

  // Emitted when the user has finished editing the payout destination within the mobile entry point
  exitentrypoint: Record<string, never>;
};

export type ConnectElementEventType = keyof ConnectElementEventTypes;

export type ConnectElementEventValue =
  ConnectElementEventTypes[ConnectElementEventType];

export interface ICalculatedConnectElementProperties
  extends IConnectElementProperties {
  elementTagName: string;
}

export const commonHtmlEventMethods: Record<string, CustomHtmlElementMethod> = {
  setAccountSessionClaimed: {
    type: 'EventListenerMethod',
    event: 'accountsessionclaimed',
    isInternal: true,
    description:
      'Sets the event listener for when an account session is claimed. This returns the connected account ID used for analytics with the mobile SDK.',
  },
  setCollectMobileFinancialConnectionsResult: {
    type: 'SupplementalObject',
    objectToSet: 'mobileFinancialConnectionsResult',
    isInternal: true,
    description:
      'Sets the result of the financial connections native SDK call. Used to return the connections data to components.',
  },
  setOnLoadError: {
    type: 'EventListenerMethod',
    event: 'loaderror',
    isInternal: false,
    description: 'Sets the event listener for when a component fails to load',
  },
  setOnLoaderStart: {
    type: 'EventListenerMethod',
    event: 'loaderstart',
    isInternal: false,
    description:
      'Sets the event listener for when a component starts to display UI',
  },
  setOnExitEntryPoint: {
    type: 'EventListenerMethod',
    event: 'exitentrypoint',
    isInternal: true,
    description:
      'Sets the event listener for when a user triggers an action signaling exiting a mobile entry point',
  },
  setShowTaxInvoicesOnly: {
    type: 'SupplementalObject',
    objectToSet: 'showTaxInvoicesOnly',
    isInternal: true,
    description:
      'Restricts the Documents list to only fetch and render tax invoices (VAT invoices).',
  },
};
